/**
 * Version Utility
 *
 * Single source of truth for application version.
 * Version is automatically injected at build time from package.json.
 *
 * Usage:
 *   import { getVersion, getFullVersionString } from './utils/version';
 *   const version = getVersion(); // "0.17.0"
 *   const fullVersion = getFullVersionString(); // "Sparvi Cloud v0.17.0"
 */

// Version is injected at build time by webpack/react-scripts
// It reads from package.json during the build process
export const APP_VERSION = process.env.REACT_APP_VERSION || '0.17.0';

// Build timestamp (injected at build time)
export const BUILD_TIMESTAMP = process.env.REACT_APP_BUILD_TIME || new Date().toISOString();

// Environment
export const APP_ENV = process.env.NODE_ENV || 'development';

/**
 * Get the current application version
 * @returns {string} Version string (e.g., "0.17.0")
 */
export const getVersion = () => {
  return APP_VERSION;
};

/**
 * Get formatted version string with app name
 * @returns {string} Full version string (e.g., "Sparvi Cloud v0.17.0")
 */
export const getFullVersionString = () => {
  return `Sparvi Cloud v${APP_VERSION}`;
};

/**
 * Get short version string (e.g., "v0.17.0")
 * @returns {string} Short version with v prefix
 */
export const getShortVersion = () => {
  return `v${APP_VERSION}`;
};

/**
 * Get version with environment suffix
 * @returns {string} Version with environment (e.g., "v0.17.0 (dev)")
 */
export const getVersionWithEnv = () => {
  const envSuffix = APP_ENV === 'production' ? '' : ` (${APP_ENV})`;
  return `v${APP_VERSION}${envSuffix}`;
};

/**
 * Get build information object
 * @returns {object} Build info including version, timestamp, environment
 */
export const getBuildInfo = () => {
  return {
    version: APP_VERSION,
    buildTime: BUILD_TIMESTAMP,
    environment: APP_ENV,
    fullVersion: getFullVersionString()
  };
};

/**
 * Check if this is a production build
 * @returns {boolean}
 */
export const isProduction = () => {
  return APP_ENV === 'production';
};

/**
 * Get human-readable build time
 * @returns {string} Formatted build time
 */
export const getBuildTime = () => {
  try {
    const date = new Date(BUILD_TIMESTAMP);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'Unknown';
  }
};
